import React, { useState } from "react";
import { IconButton, Tooltip } from "@mui/material";

import { ClipboardIcon } from "assets";
import { getBillSupportingDocuments, BillSupportingDocument } from "api";
import {
  useNotify,
  useUploadBillSupportingDocument,
  useDeleteBillSupportingDocument,
} from "hooks";
import { AttachedDocsModal } from "components";

interface BillAttachedDocsButtonProps {
  billId: string;
  documentCount: number;
  iconButtonSx?: React.CSSProperties;
  // Called after a supporting document is uploaded or deleted, so a parent
  // list/table that doesn't refetch via react-query invalidation (e.g. one
  // backed by plain local state) can refresh its own data.
  onDocumentsChanged?: () => void;
}

export const BillAttachedDocsButton: React.FC<BillAttachedDocsButtonProps> = ({
  billId,
  documentCount,
  iconButtonSx = {},
  onDocumentsChanged,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [documents, setDocuments] = useState<BillSupportingDocument[]>([]);
  const notify = useNotify();
  const uploadMutation = useUploadBillSupportingDocument();
  const deleteMutation = useDeleteBillSupportingDocument();

  const fetchDocuments = async () => {
    try {
      const docs = await getBillSupportingDocuments(billId);
      setDocuments(docs);
    } catch {
      notify.error("Failed to fetch supporting documents");
      setDocuments([]);
    }
  };

  const fetchAndOpen = async () => {
    await fetchDocuments();
    setIsModalOpen(true);
  };

  // Refreshes the modal's document list without forcing it back open — used
  // after a successful upload, where the modal is being closed at the same
  // time (forcing it open here would race with that close).
  const handleDocumentsChanged = async () => {
    await fetchDocuments();
    onDocumentsChanged?.();
  };

  // Adapter: modal calls mutateAsync({ dataId, files }) → hook expects { guid, file }
  const adaptedUploadMutation = {
    mutateAsync: ({
      dataId,
      files,
    }: {
      dataId: number | string;
      files: File[];
    }) => uploadMutation.mutateAsync({ guid: String(dataId), file: files[0] }),
    isLoading: uploadMutation.isLoading,
  };

  return (
    <>
      <Tooltip arrow placement="top" title="Supporting Documents">
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            fetchAndOpen();
          }}
          sx={{
            width: "26px",
            height: "20px",
            padding: 0,
            position: "relative",
            ...iconButtonSx,
            "&:hover": {
              backgroundColor: "rgba(5, 66, 84, 0.1)",
            },
          }}
        >
          <ClipboardIcon
            style={{
              width: "16px",
              height: "15px",
              color: documentCount > 0 ? "#46A314" : "#054254",
            }}
          />
          {documentCount > 0 && (
            <span
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                fontSize: "10px",
                fontWeight: 600,
                color: "#46A314",
                lineHeight: 1,
              }}
            >
              {documentCount}
            </span>
          )}
        </IconButton>
      </Tooltip>

      {isModalOpen && (
        <AttachedDocsModal
          isDialogOpen={isModalOpen}
          onDialogClose={() => setIsModalOpen(false)}
          dataId={billId}
          documents={documents}
          uploadMutation={adaptedUploadMutation}
          deleteMutation={deleteMutation}
          onUploadSuccess={handleDocumentsChanged}
          onDocumentDelete={handleDocumentsChanged}
        />
      )}
    </>
  );
};
